// Skills tab: the team-level port of the OSS skill-health view.
//
// Roster (one row per skill NAME across the scope) → per-skill detail. The
// distinctive team column is VARIANTS: how many distinct contents (body hashes)
// of this skill name are installed across members. 1 = the team agrees; more =
// fragmentation — the detail view shows who is on which variant and how each
// variant's activation outcomes compare, which is the "converge on the version
// that works" signal no single-user view can produce.
//
// One container (#skills) re-rendered per navigation. Selection lives in
// state.skills {name} and mirrors into the URL (?skill=…&win=…) so a drill-in
// is a shareable, reload-survivable link. Honesty rules carried over from OSS:
// outcomes are observational (used/reworked/ignored, "bypassed" = reworked +
// ignored), insufficient-context firings are footnoted and never counted, and
// variant comparisons are correlation, not causation.
import { state, $, esc, get, num, scopeQs } from './core'
import { syncHash } from './router'
import { openDetail } from './sessions'
import { mountScopeBar, hasScopeControls, refreshFilterOptions } from './filterbar'

// ---- router integration -----------------------------------------------------

// Window presets follow the sessions tab's vocabulary.
var skillWin: string = '30';
// STATUS filter (client-side over the fetched roster): '' = Used (default),
// 'all', 'unused', 'not-in-config', 'often-bypassed'. Mirrors OSS.
var skStatus: string = '';
// AGENT (harness) filter — server-side, so a change re-fetches. '' = all.
var skSource: string = '';
// Name search — ephemeral (not URL-persisted), client-side substring.
var skSearch: string = '';
// Last roster payload, so a STATUS/search change re-renders without re-fetching.
var lastRoster: any = null;
// Version-compare selection for the detail's drift card (both are folderHashes).
// Module state, NOT URL-persisted, and cleared on skill nav so a comparison
// picked on one skill never leaks into the next one.
var vA: string = '';
var vB: string = '';

export function getSkillParams(): Record<string, string> {
  var q: Record<string, string> = {};
  if (state.skills.name) q.skill = state.skills.name;
  if (skillWin !== '30') q.win = skillWin;
  if (skStatus) q.status = skStatus;
  if (skSource) q.agent = skSource;
  // The team axis of the shared scope, mirrored into the URL so a team-scoped
  // skills view is a shareable deep link and survives Back/Forward. Only `team`
  // is URL-persisted here (repo/user stay session-global) — that's the axis the
  // team-metric detail page carries in (see costArtifact.gotoSkill).
  if (state.scope.team) q.team = state.scope.team;
  return q;
}

export function applySkillParams(query: Record<string, string>): void {
  state.skills = { name: query.skill || '' };
  skillWin = /^(7|14|30|90|all)$/.test(query.win || '') ? query.win : '30';
  skStatus = /^(all|unused|not-in-config|often-bypassed)$/.test(query.status || '') ? query.status : '';
  skSource = query.agent || '';
  // The URL is authoritative for the team scope on this route (present ⇒ that
  // team, absent ⇒ all teams). A change behaves exactly like the scope bar's own
  // team switch — reset the old team's repo/user narrowing and refresh the option
  // lists — so a URL-driven change and a click-driven one land in the same state.
  var team = query.team || '';
  if (team !== state.scope.team) {
    state.scope.team = team;
    state.scope.repos = [];
    if (state.scope.user && state.scope.user !== 'me') state.scope.user = '';
    refreshFilterOptions();
  }
  renderSkills();
}

function nav(name: string): void {
  state.skills = { name: name };
  vA = ''; vB = ''; // a new skill starts with no version comparison
  syncHash();
  renderSkills();
}

function daysQs(): string {
  return 'days=' + encodeURIComponent(skillWin === 'all' ? 'all' : skillWin);
}

function sourceQs(): string {
  return skSource ? '&source=' + encodeURIComponent(skSource) : '';
}

// ---- shared bits ------------------------------------------------------------

function shortHash(h: string | null): string {
  return h ? h.slice(0, 8) : 'unknown';
}

// A GitHub deep-link to a skill AT a specific commit: the skill folder at that
// commit when it's a subdir, or the repo root at that commit when the skill IS the
// repo (path ''). repo/commit/path are server-provided provenance; esc guards the
// attribute. (An empty path is repo-root here, never "unknown" — provenance always
// carries a path alongside a repo.)
function commitHref(repo: string, commit: string, path: string | null): string {
  var base = 'https://github.com/' + esc(repo) + '/tree/' + esc(commit);
  return path ? base + '/' + esc(path) : base;
}

// A neutral chip for a version's position vs. its repo's canonical commit. Only
// rendered for a resolved standing (an untracked/off-branch version shows nothing).
// "Behind" carries the commit count when known, else a bare "behind".
function standingChip(v: any): string {
  if (v.standing === 'canonical') return '<span class="skd-st skd-st-ok">on canonical</span>';
  if (v.standing === 'diverged') return '<span class="skd-st skd-st-div">local edits</span>';
  if (v.standing === 'behind') {
    var n = v.behindCommits;
    var label = typeof n === 'number' && n > 0 ? (n === 1 ? '1 commit behind' : n + ' commits behind') : 'behind';
    return '<span class="skd-st skd-st-behind">' + label + '</span>';
  }
  return '';
}

function pct(n: number, d: number): string {
  return d > 0 ? Math.round((n / d) * 100) + '%' : '—';
}

function dayOf(s: string | null): string {
  return s ? String(s).slice(0, 10) : '—';
}

function winSeg(): string {
  var opts = [['7', '7d'], ['14', '14d'], ['30', '30d'], ['90', '90d'], ['all', 'All']];
  return '<div class="seg" id="skillWinSeg">' + opts.map(function (o) {
    return '<button data-win="' + o[0] + '" class="' + (skillWin === o[0] ? 'on' : '') + '">' + o[1] + '</button>';
  }).join('') + '</div>';
}

function wireWinSeg(): void {
  var seg = document.getElementById('skillWinSeg');
  if (!seg) return;
  Array.prototype.forEach.call(seg.querySelectorAll('button'), function (b: HTMLElement) {
    b.onclick = function () {
      skillWin = b.getAttribute('data-win') || '30';
      syncHash({ replace: true });
      renderSkills();
    };
  });
}

// ---- roster -----------------------------------------------------------------

var STATUS_META: Record<string, { color: string }> = {
  used: { color: 'var(--emerald)' },
  unused: { color: '#b7b7ad' },
};
var FLAG_META: Record<string, { cls: string; label: string; tip: string }> = {
  'not-in-config': { cls: 'sk-flag-blue', label: 'not in config', tip: 'Invoked, but not installed by any reporting member in scope' },
  'often-bypassed': { cls: 'sk-flag-amber', label: 'often bypassed', tip: 'At least 5 judged firings, and the agent went around the skill in half or more' },
};

function sourceLabel(s: string): string {
  var m: Record<string, string> = { 'claude-code': 'Claude Code', codex: 'Codex', opencode: 'OpenCode', pi: 'Pi' };
  return m[s] || s;
}

// A tiny per-bucket bar sparkline of usage over the window.
function skSparkline(spark: number[]): string {
  if (!spark || !spark.length) return '<span class="sk-spark"></span>';
  var max = spark.reduce(function (a, b) { return Math.max(a, b); }, 0) || 1;
  var W = 96, H = 24, n = spark.length, step = W / n, bw = Math.max(1.5, step - 1);
  var bars = '';
  for (var i = 0; i < n; i++) {
    var v = spark[i] || 0;
    if (!v) continue;
    var h = Math.max(2, Math.round((v / max) * H));
    bars += '<rect x="' + (i * step).toFixed(1) + '" y="' + (H - h) + '" width="' + bw.toFixed(1) + '" height="' + h + '" rx="0.6"></rect>';
  }
  return '<svg class="sk-spark" width="' + W + '" height="' + H + '" viewBox="0 0 ' + W + ' ' + H + '" aria-hidden="true">' + bars + '</svg>';
}

function flagChips(flags: string[]): string {
  return (flags || []).map(function (f) {
    var m = FLAG_META[f];
    return m ? '<span class="sk-flag ' + m.cls + '" title="' + esc(m.tip) + '">' + esc(m.label) + '</span>' : '';
  }).join('');
}

function metricCell(value: string, label: string, title?: string): string {
  return '<div class="sk-metric"' + (title ? ' title="' + esc(title) + '"' : '') +
    '><div class="sk-metric-v">' + value + '</div><div class="sk-metric-l">' + esc(label) + '</div></div>';
}

function debounce(fn: () => void, ms: number): () => void {
  var t: any = null;
  return function () { if (t) clearTimeout(t); t = setTimeout(fn, ms); };
}

// STATUS is a client-side filter over the fetched roster (which is already
// scope/window/source-scoped server-side): '' = used only (default), 'all',
// 'unused', or a flag key. Search is a name substring.
function filterRows(rows: any[]): any[] {
  var q = skSearch.trim().toLowerCase();
  return rows.filter(function (row: any) {
    var pass: boolean;
    if (skStatus === '') pass = row.status === 'used';
    else if (skStatus === 'all') pass = true;
    else if (skStatus === 'unused') pass = row.status === 'unused';
    else pass = (row.flags || []).indexOf(skStatus) >= 0;
    if (!pass) return false;
    if (q && String(row.name).toLowerCase().indexOf(q) < 0) return false;
    return true;
  });
}

export function renderSkills(): void {
  var el = $('#skills');
  if (!el) return;
  if (state.skills.name) return renderDetail(el, state.skills.name);

  get('/api/skills?' + daysQs() + sourceQs() + scopeQs()).then(function (r) {
    if (state.view !== 'skills' || state.skills.name) return; // navigated away meanwhile
    lastRoster = r || { rows: [] };
    paintRoster(el);
  });
}

// Full paint: window + scope + summary tiles + filter row + the list container.
// STATUS/search changes repaint only the list (renderList) so the search input
// keeps focus; source/window/scope changes re-fetch (renderSkills).
function paintRoster(el: any): void {
  var r = lastRoster || { rows: [] };
  var rows = r.rows || [];
  var html = '<section class="panel"><div class="panel-head"><h2>Skills</h2>' + winSeg() + '</div>';
  if (hasScopeControls()) html += '<div class="filter-bar sk-scope" id="skills-fb"></div>';

  if (!rows.length) {
    html += '<div class="empty">No skills observed yet. Skills appear here once members\' sessions invoke them, ' +
      'or the ingest client reports what\'s installed (it does so automatically after each upload).</div></section>';
    el.innerHTML = html;
    wireWinSeg();
    mountSkillsScope();
    return;
  }

  html += summaryTiles(r);
  html += filterRow(r);
  html += '<div class="sk-list" id="sk-list"></div>';
  html += '<div class="card-note">Outcomes are observational — what the agent did with the skill\'s output, never a claim the skill caused it. ' +
    '"Installed by" counts only machines that have reported an inventory.</div></section>';
  el.innerHTML = html;

  wireWinSeg();
  mountSkillsScope();
  wireFilters(el, r);
  renderList(el, r);
}

function mountSkillsScope(): void {
  var fb = document.getElementById('skills-fb');
  // A scope change now moves the URL too (the team axis is URL-persisted), so
  // mirror the winSeg/status pattern: write the hash, then repaint.
  if (fb) mountScopeBar(fb as HTMLElement, function () { syncHash({ replace: true }); renderSkills(); });
}

function summaryTiles(r: any): string {
  // Inline stat row, OSS-style: big number + small uppercase label, the active
  // filter highlighted. The counts OVERLAP (Installed ⊇ Used∩Installed + Unused;
  // Used ⊇ that + Not-in-config), so these are filters, not a partition.
  var tiles: Array<{ k: string; v: number; l: string }> = [
    { k: 'all', v: r.totalInstalled || 0, l: 'Installed' },
    { k: '', v: r.totalUsed || 0, l: 'Used' },
    { k: 'unused', v: r.totalUnused || 0, l: 'Unused' },
  ];
  if (r.totalNotInConfig > 0) tiles.push({ k: 'not-in-config', v: r.totalNotInConfig, l: 'Not in config' });
  if (r.totalOftenBypassed > 0) tiles.push({ k: 'often-bypassed', v: r.totalOftenBypassed, l: 'Often bypassed' });
  return '<div class="sk-stats">' + tiles.map(function (t) {
    return '<button type="button" class="sk-stat' + (skStatus === t.k ? ' on' : '') + '" data-status="' + t.k + '">' +
      '<span class="sk-stat-v">' + num(t.v) + '</span><span class="sk-stat-l">' + esc(t.l) + '</span></button>';
  }).join('') + '</div>';
}

function filterRow(r: any): string {
  var statusOpts: Array<[string, string]> = [['', 'Used (default)'], ['all', 'All statuses'], ['unused', 'Unused']];
  if (r.totalNotInConfig > 0) statusOpts.push(['not-in-config', 'Not in config']);
  if (r.totalOftenBypassed > 0) statusOpts.push(['often-bypassed', 'Often bypassed']);
  var statusSel = '<label class="fb-lbl">Status</label><select id="sk-status" class="fb-sel">' +
    statusOpts.map(function (o) { return '<option value="' + o[0] + '"' + (skStatus === o[0] ? ' selected' : '') + '>' + esc(o[1]) + '</option>'; }).join('') +
    '</select>';
  var agentSel = '';
  if ((r.availableSources || []).length > 1) {
    agentSel = '<label class="fb-lbl">Agent</label><select id="sk-source" class="fb-sel"><option value="">All agents</option>' +
      r.availableSources.map(function (s: string) { return '<option value="' + esc(s) + '"' + (skSource === s ? ' selected' : '') + '>' + esc(sourceLabel(s)) + '</option>'; }).join('') +
      '</select>';
  }
  return '<div class="sk-filters">' +
    '<input id="sk-search" class="fb-in sk-search" type="search" placeholder="search skill name" value="' + esc(skSearch) + '" />' +
    statusSel + agentSel + '</div>';
}

// The list + caption only — repainted on STATUS/search change without touching
// the filter inputs (so the search box keeps focus and caret position).
function renderList(el: any, r: any): void {
  var rows = r.rows || [];
  var shown = filterRows(rows);
  var list = document.getElementById('sk-list');
  if (!list) return;
  if (!shown.length) { list.innerHTML = '<div class="empty">No skills match this filter.</div>'; return; }
  list.innerHTML = shown.map(skRow).join('');
  Array.prototype.forEach.call(list.querySelectorAll('.sk-row'), function (rowEl: HTMLElement) {
    rowEl.onclick = function () { nav(rowEl.getAttribute('data-skill') || ''); };
  });
}

function skRow(row: any): string {
  var meta = STATUS_META[row.status] || STATUS_META.unused;
  var variants = row.variants > 1
    ? '<span class="sk-flag sk-flag-amber" title="' + row.variants + ' distinct contents of this skill installed across the scope">' + row.variants + ' variants</span>'
    : '';
  var errors = row.errorCalls ? '<span class="sk-err" title="' + row.errorCalls + ' errored call(s)">' + num(row.errorCalls) + ' err</span>' : '';
  return '<div class="sk-row" data-skill="' + esc(row.name) + '">' +
    '<span class="sk-dot" style="background:' + meta.color + '" title="' + esc(row.status) + '"></span>' +
    '<div class="sk-main"><div class="sk-name">' + esc(row.name) + variants + flagChips(row.flags) + '</div>' +
    (row.description ? '<div class="sk-desc">' + esc(row.description) + '</div>' : '') + '</div>' +
    '<div class="sk-right">' + skSparkline(row.spark) +
    metricCell(num(row.usedBy), 'users', 'Members who invoked it in the window') +
    metricCell(num(row.calls) + (errors ? ' ' + errors : ''), 'calls') +
    metricCell(num(row.sessions), 'sessions') +
    '<span class="sk-caret">›</span></div></div>';
}

function wireFilters(el: any, r: any): void {
  Array.prototype.forEach.call(el.querySelectorAll('.sk-stat'), function (t: HTMLElement) {
    t.onclick = function () { setStatus(el, r, t.getAttribute('data-status') || ''); };
  });
  var statusSel = document.getElementById('sk-status') as HTMLSelectElement | null;
  if (statusSel) statusSel.onchange = function () { setStatus(el, r, statusSel!.value); };
  var srcSel = document.getElementById('sk-source') as HTMLSelectElement | null;
  if (srcSel) srcSel.onchange = function () { skSource = srcSel!.value; syncHash({ replace: true }); renderSkills(); };
  var search = document.getElementById('sk-search') as HTMLInputElement | null;
  if (search) search.oninput = debounce(function () {
    var s = document.getElementById('sk-search') as HTMLInputElement | null;
    skSearch = s ? s.value : '';
    renderList(el, r);
  }, 150);
}

// Apply a STATUS change from either a tile or the select: keep both controls in
// sync, persist to the URL, and repaint just the list.
function setStatus(el: any, r: any, status: string): void {
  skStatus = status;
  syncHash({ replace: true });
  Array.prototype.forEach.call(el.querySelectorAll('.sk-stat'), function (t: HTMLElement) {
    t.classList.toggle('on', (t.getAttribute('data-status') || '') === status);
  });
  var statusSel = document.getElementById('sk-status') as HTMLSelectElement | null;
  if (statusSel && statusSel.value !== status) statusSel.value = status;
  renderList(el, r);
}

// ---- detail -----------------------------------------------------------------
//
// The per-skill drill-in, ported section-for-section from OSS "Skill Health":
// tiles → usage trend → activation outcomes → version drift → where-used →
// co-occurrence → details → invocations. The one bit of local state is the
// version-compare pair (vA/vB); a pick re-fetches the SAME window/scope so the
// drift card can recompute against the chosen versions server-side.

// Re-render the open detail in place after a version-compare pick. Deliberately
// leaves the window + scope untouched — only vA/vB changed.
function reloadDetail(): void {
  var el = $('#skills');
  if (el && state.skills.name) renderDetail(el, state.skills.name);
}

// Window word for the caption/subtitles ("30 days", "all time").
function winLabel(): string {
  return skillWin === 'all' ? 'all time' : skillWin + ' days';
}

// Bucket granularity word for the usage-trend caption, read off the buckets.
function trendGran(buckets: any[]): string {
  if (!buckets || !buckets.length) return 'period';
  var days = (buckets[0].endMs - buckets[0].startMs) / 86400000;
  return days <= 1.5 ? 'day' : days <= 8 ? 'week' : 'month';
}

// A bucket's date-range label for the hover tooltip ("Jul 8" or "Jul 8 – Jul 14").
function bucketRange(b: any): string {
  var fmt = function (ms: number) { return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }); };
  var days = (b.endMs - b.startMs) / 86400000;
  return days <= 1.5 ? fmt(b.startMs) : fmt(b.startMs) + ' – ' + fmt(b.endMs - 86400000);
}

function fracPct(x: number): string { return Math.round((x || 0) * 100) + '%'; }
function fmtWk(x: number): string { return (Math.round((x || 0) * 10) / 10) + '/wk'; }

// A headline tile, value-first (big number, then label, then sub) — OSS-style.
function dTile(label: string, value: string, sub: string): string {
  return '<div class="skd-tile"><div class="skd-tile-v">' + value + '</div>' +
    '<div class="skd-tile-l">' + esc(label) + '</div>' +
    (sub ? '<div class="skd-tile-s">' + esc(sub) + '</div>' : '') + '</div>';
}

function renderDetail(el: any, name: string): void {
  var url = '/api/skills/' + encodeURIComponent(name) + '?' + daysQs() + scopeQs();
  if (vA) url += '&va=' + encodeURIComponent(vA);
  if (vB) url += '&vb=' + encodeURIComponent(vB);
  get(url).then(function (d) {
    if (state.view !== 'skills' || state.skills.name !== name) return;
    var back = '<button type="button" class="tag click" id="skillBack">← All skills</button>';
    // The shared scope bar, on the detail too (not just the roster) so the team
    // is visible and changeable here — and so a deep link / team-metric drill-in
    // that carries a team reads back its filter. Omitted when the viewer has no
    // scope control to offer (same gate the roster uses).
    var scopeBar = hasScopeControls() ? '<div class="filter-bar sk-scope" id="skills-detail-fb"></div>' : '';
    if (!d || d.error) {
      el.innerHTML = '<div class="skd-topbar">' + back + winSeg() + '</div>' + scopeBar +
        '<section class="panel"><div class="panel-head"><h2>' + esc(name) + '</h2></div>' +
        '<div class="empty">No data for this skill in the current scope/window.</div></section>';
      wireWinSeg();
      wireBack();
      mountSkillsDetailScope();
      return;
    }

    // 1. Header (OSS-style, on the page background): the back link + time filter
    // bar, then a status dot + name + status word, the full description, and the
    // three headline tiles.
    var on = d.calls > 0;
    var html = '<div class="skd-topbar">' + back + winSeg() + '</div>' + scopeBar +
      '<div class="skd-title"><span class="skd-title-dot ' + (on ? 'on' : 'off') + '"></span>' +
      '<h2 class="skd-title-name">' + esc(name) + '</h2>' +
      '<span class="skd-title-status ' + (on ? 'skd-status-on' : 'skd-status-off') + '">' + (on ? 'USED' : 'UNUSED') + '</span></div>' +
      (d.description ? '<p class="skd-desc">' + esc(d.description) + '</p>' : '') +
      detailTiles(d);

    html += usageCard(d);
    html += outcomesCard(d);
    html += versionsCard(d);
    html += whereUsedCard(d);
    html += cooccurCard(d);
    html += detailsCard(d);
    html += invocationsCard(d);

    el.innerHTML = html;
    wireDetail(el);
  });
}

// 2. Three headline tiles: invocations, sessions, own-call error rate.
function detailTiles(d: any): string {
  var errRate = d.calls > 0 ? Math.round((d.errorCalls / d.calls) * 100) + '%' : '—';
  var errSub = d.calls > 0 ? num(d.errorCalls) + ' of ' + num(d.calls) + ' calls errored' : '—';
  var invSub = skillWin === 'all' ? 'across all time' : 'in the last ' + winLabel();
  return '<section class="tiles skd-tiles">' +
    dTile('Invocations', num(d.calls), invSub) +
    dTile('Sessions', num(d.sessions), 'distinct sessions') +
    dTile('Own-call error rate', errRate, errSub) +
    '</section>';
}

// A section heading (OSS-style: modest serif, not oversized) with an optional
// hover tooltip — the little circled "?" that explains what the section shows.
function secHead(title: string, tip?: string): string {
  var help = tip ? ' <span class="skd-help" data-tip="' + esc(tip) + '">?</span>' : '';
  return '<div class="panel-head"><h2>' + esc(title) + help + '</h2></div>';
}

// 3. Usage trend: a count bar chart with a Y axis (count ticks + gridlines),
// evenly-spaced calendar x-ticks, and a hover tooltip — a real chart, not the
// roster's bare sparkline. Bars align 1:1 with the report's spark buckets.
function usageChart(spark: number[], buckets: any[]): string {
  var vals = spark || [];
  var n = buckets && buckets.length ? buckets.length : vals.length;
  if (!n) return '<div class="empty">No usage in this window.</div>';
  var max = 0;
  for (var i = 0; i < n; i++) if ((vals[i] || 0) > max) max = vals[i] || 0;
  max = max || 1;
  var yTicks: number[] = [];
  if (max <= 4) { for (var t = 0; t <= max; t++) yTicks.push(t); }
  else yTicks = [0, Math.round(max / 2), max];
  var W = 860, H = 220, padL = 38, padR = 12, padT = 14, padB = 34;
  var plotW = W - padL - padR, plotH = H - padT - padB, base = padT + plotH, bw = plotW / n;
  var yOf = function (v: number) { return padT + (1 - v / max) * plotH; };
  var svg = '<svg class="skd-trend-svg" viewBox="0 0 ' + W + ' ' + H + '" width="100%" preserveAspectRatio="xMidYMid meet">';
  yTicks.forEach(function (v) {
    var y = yOf(v);
    svg += '<line class="skd-trend-grid" x1="' + padL + '" y1="' + y.toFixed(1) + '" x2="' + (W - padR) + '" y2="' + y.toFixed(1) + '"></line>';
    svg += '<text class="skd-trend-ax" x="' + (padL - 6) + '" y="' + (y + 3).toFixed(1) + '" text-anchor="end">' + v + '</text>';
  });
  var xStep = Math.max(1, Math.ceil(n / 8));
  for (var b = 0; b < n; b++) {
    var x = padL + b * bw, w = Math.max(2, bw - 3), bx = x + (bw - w) / 2;
    var bk = buckets && buckets[b];
    var c = vals[b] || 0;
    svg += '<rect class="skd-trend-hit" x="' + x.toFixed(1) + '" y="' + padT + '" width="' + bw.toFixed(1) + '" height="' + plotH +
      '" data-range="' + esc(bk ? bucketRange(bk) : '') + '" data-count="' + esc(num(c) + ' invocation' + (c === 1 ? '' : 's')) + '"></rect>';
    if (c > 0) {
      var top = yOf(c);
      svg += '<rect class="skd-trend-bar" x="' + bx.toFixed(1) + '" y="' + top.toFixed(1) + '" width="' + w.toFixed(1) +
        '" height="' + Math.max(1, base - top).toFixed(1) + '" rx="2"></rect>';
    }
    if (bk && (b % xStep === 0 || b === n - 1)) {
      var lbl = new Date(bk.startMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      var anchor = b === 0 ? 'start' : b === n - 1 ? 'end' : 'middle';
      var tx = anchor === 'start' ? padL : anchor === 'end' ? W - padR : x + bw / 2;
      svg += '<text class="skd-trend-ax" x="' + tx.toFixed(1) + '" y="' + (H - 12) + '" text-anchor="' + anchor + '">' + esc(lbl) + '</text>';
    }
  }
  svg += '</svg>';
  return '<div class="skd-trend" id="skd-trend">' + svg + '</div>';
}

function usageCard(d: any): string {
  return '<section class="panel skd-sec">' + secHead('Usage trend', 'How often this skill fired over time.') +
    usageChart(d.spark || [], d.sparkBuckets || []) +
    '<div class="card-note">Invocations per ' + trendGran(d.sparkBuckets || []) + ' over the window. Hover a bar for its exact count.</div></section>';
}

// The floating tooltip for the usage chart — date range + exact count on hover.
function wireTrendTooltip(): void {
  var host = document.getElementById('skd-trend');
  if (!host) return;
  var tip = document.createElement('div');
  tip.className = 'skd-trend-tip';
  tip.style.display = 'none';
  host.appendChild(tip);
  Array.prototype.forEach.call(host.querySelectorAll('.skd-trend-hit'), function (hit: any) {
    hit.onmousemove = function (e: any) {
      tip.innerHTML = '<b>' + esc(hit.getAttribute('data-range')) + '</b><br>' + esc(hit.getAttribute('data-count'));
      tip.style.display = 'block';
      var wr = host.getBoundingClientRect();
      tip.style.left = (e.clientX - wr.left + 12) + 'px';
      tip.style.top = (e.clientY - wr.top + 12) + 'px';
    };
    hit.onmouseleave = function () { tip.style.display = 'none'; };
  });
}

// Evidence-row label + colour per outcome (matches the legend / stacked bar).
var OUTCOME_EV: Record<string, { label: string; cls: string }> = {
  used: { label: 'Followed', cls: 'skd-oc-good' },
  reworked: { label: 'Reworked', cls: 'skd-oc-warn' },
  ignored: { label: 'Bypassed', cls: 'skd-oc-bad' },
  unclear: { label: 'Unclear', cls: 'skd-oc-grey' },
};

// 4. Activation outcomes: bypass headline + a stacked bar with a legend, an
// optional user-correction note, an evidence list, and a judged-count footnote.
function outcomesCard(d: any): string {
  var o = d.outcomes;
  if (!o || !o.classified) return '';
  var cls = o.classified;
  var bypassRate = o.bypassed / cls;
  var headline = bypassRate >= 0.5 ? 'Often bypassed' : bypassRate >= 0.2 ? 'Sometimes reworked' : 'Mostly followed';
  var html = '<section class="panel skd-sec">' + secHead('Activation outcomes', "Whether the agent kept, reworked, or bypassed the skill's output.") +
    '<p class="skd-headline">' + esc(headline) + '</p>' +
    '<p class="skd-sub">The agent bypassed or reworked its output in ' + num(o.bypassed) + ' of ' + num(cls) +
    ' judged invocations (' + pct(o.bypassed, cls) + ').</p>';

  // Segments: used=Followed(green), reworked(amber), ignored=Bypassed(red),
  // unclear(grey).
  var segs: Array<{ label: string; v: number; cls: string }> = [
    { label: 'Followed', v: o.used || 0, cls: 'skd-seg-good' },
    { label: 'Reworked', v: o.reworked || 0, cls: 'skd-seg-warn' },
    { label: 'Bypassed', v: o.ignored || 0, cls: 'skd-seg-bad' },
    { label: 'Unclear', v: o.unclear || 0, cls: 'skd-seg-grey' },
  ];
  var bar = '<div class="skd-stack">';
  segs.forEach(function (s) {
    if (s.v <= 0) return;
    bar += '<span class="skd-stack-seg ' + s.cls + '" style="width:' + ((s.v / cls) * 100).toFixed(2) +
      '%" title="' + esc(s.label + ' ' + s.v) + '"></span>';
  });
  bar += '</div>';
  var legend = '<div class="skd-legend">' + segs.map(function (s) {
    return '<span class="skd-leg"><i class="skd-sw ' + s.cls + '"></i>' + esc(s.label) + ' ' +
      pct(s.v, cls) + ' (' + num(s.v) + ')</span>';
  }).join('') + '</div>';
  html += bar + legend;

  if (o.userCorrectionAdjacent > 0) {
    html += '<p class="skd-warn">⚠ A user correction landed adjacent to ' + num(o.userCorrectionAdjacent) +
      ' of ' + num(cls) + ' invocations.</p>';
  }

  // Evidence — only the actionable cases (reworked / bypassed); the bar + legend
  // already cover the "followed" share, and a Followed example isn't actionable.
  var actionable = (d.outcomeExamples || []).filter(function (e: any) {
    return e.outcome === 'reworked' || e.outcome === 'ignored';
  });
  if (actionable.length) {
    html += '<div class="skd-ev">' + actionable.map(function (e: any, i: number) {
      var m = OUTCOME_EV[e.outcome] || OUTCOME_EV.unclear;
      var rest = i >= 3 ? ' skd-ev-rest' : '';
      // A session the viewer can't open (RBAC) is greyed and inert — same rule
      // the Sessions tab enforces; only the linkable ones carry data-session.
      var locked = e.accessible === false;
      var attr = locked ? '' : ' data-session="' + esc(e.sessionId) + '" data-idx="' + esc(String(e.idx)) + '"';
      var go = locked ? '<span class="skd-ev-lock" title="You don\'t have access to this session">restricted</span>' : '<span class="skd-ev-go">open ↗</span>';
      return '<div class="skd-ev-row' + rest + (locked ? ' skd-locked' : '') + '"' + attr + '>' +
        '<span class="skd-ev-out ' + m.cls + '">' + esc(m.label) + '</span>' +
        '<span class="skd-ev-txt">' + esc(e.evidence || '') + '</span>' + go + '</div>';
    }).join('') + '</div>';
    if (actionable.length > 3) {
      html += '<button type="button" class="skd-more" id="skd-ev-more">Show ' + (actionable.length - 3) + ' more</button>';
    }
  }

  var foot = 'Judged ' + num(cls) + ' invocations in this window.';
  if (o.insufficientContext > 0) foot += ' (' + num(o.insufficientContext) + ' had insufficient context, not counted)';
  html += '<div class="card-note">' + esc(foot) + '</div></section>';
  return html;
}

// A minimal LCS line diff → rows tagged add / del / ctx. Small enough to inline;
// gives a clean removed/added/unchanged read for the body + description diffs.
function lineDiff(before: string, after: string): Array<{ t: string; s: string }> {
  var a = String(before || '').split('\n');
  var b = String(after || '').split('\n');
  var n = a.length, m = b.length;
  var dp: number[][] = [];
  for (var i = 0; i <= n; i++) {
    var row: number[] = [];
    for (var j = 0; j <= m; j++) row.push(0);
    dp.push(row);
  }
  for (var i2 = n - 1; i2 >= 0; i2--) {
    var cur = dp[i2] as number[];
    var nxt = dp[i2 + 1] as number[];
    for (var j2 = m - 1; j2 >= 0; j2--) {
      cur[j2] = a[i2] === b[j2] ? (nxt[j2 + 1] as number) + 1 : Math.max(nxt[j2] as number, cur[j2 + 1] as number);
    }
  }
  var rows: Array<{ t: string; s: string }> = [];
  var ci = 0, cj = 0;
  while (ci < n && cj < m) {
    if (a[ci] === b[cj]) { rows.push({ t: 'ctx', s: a[ci] as string }); ci++; cj++; }
    else if (((dp[ci + 1] as number[])[cj] as number) >= ((dp[ci] as number[])[cj + 1] as number)) { rows.push({ t: 'del', s: a[ci] as string }); ci++; }
    else { rows.push({ t: 'add', s: b[cj] as string }); cj++; }
  }
  while (ci < n) { rows.push({ t: 'del', s: a[ci] as string }); ci++; }
  while (cj < m) { rows.push({ t: 'add', s: b[cj] as string }); cj++; }
  return rows;
}

function diffHtml(before: string, after: string): string {
  var rows = lineDiff(before, after);
  return '<div class="skd-diff">' + rows.map(function (r) {
    var sign = r.t === 'add' ? '+' : r.t === 'del' ? '-' : ' ';
    return '<div class="skd-dl skd-dl-' + r.t + '"><span class="skd-dg">' + sign + '</span>' +
      '<span class="skd-dt">' + esc(r.s) + '</span></div>';
  }).join('') + '</div>';
}

// Version identity + first-seen time, for numbering and sorting.
function vid(v: any): string { return v.folderHash || v.bodyHash || ''; }
function vSeen(v: any): number { return v.firstSeenAt ? Date.parse(v.firstSeenAt) : 0; }

// A version's display label: V<n> by age (V1 = oldest). "Current" is meaningless
// here — several versions can be live at once (different members/repos).
function vLabel(v: any, noByHash: Record<string, number>): string {
  return 'V' + (noByHash[vid(v)] || 0);
}
function labelForHash(hash: string, versions: any[], noByHash: Record<string, number>): string {
  for (var i = 0; i < versions.length; i++) if (vid(versions[i]) === hash) return vLabel(versions[i], noByHash);
  return shortHash(hash);
}

// A "what changed vs the previous version" badge: body +adds/−dels, plus a
// description / other-files marker. Computed client-side from the version bodies.
function changeBadge(prev: any, cur: any): string {
  var bits: string[] = [];
  var add = 0, del = 0;
  lineDiff(prev.body || '', cur.body || '').forEach(function (r) {
    if (r.t === 'add') add++; else if (r.t === 'del') del++;
  });
  if (add) bits.push('<span class="skd-chg-add">+' + add + '</span>');
  if (del) bits.push('<span class="skd-chg-del">−' + del + '</span>');
  if ((prev.description || '') !== (cur.description || '')) bits.push('<span class="skd-chg-desc">description</span>');
  if (!add && !del && prev.extrasHash !== cur.extrasHash) bits.push('<span class="skd-chg-desc">other files</span>');
  return bits.length ? ' <span class="skd-vchg">' + bits.join(' ') + '</span>' : '';
}

function versionSelect(id: string, versions: any[], noByHash: Record<string, number>, selected: string): string {
  var opts = versions.slice().sort(function (a, b) { return vSeen(b) - vSeen(a); }).map(function (v: any) {
    var val = vid(v);
    var label = vLabel(v, noByHash) + ' · ' + shortHash(val) + ' · ' + num(v.adopters) + ' adopter(s)';
    return '<option value="' + esc(val) + '"' + (val === selected ? ' selected' : '') + '>' + esc(label) + '</option>';
  }).join('');
  return '<select class="skd-vsel" id="' + id + '">' + opts + '</select>';
}

// A one-line version row (OSS-style): tag · date-span + change badge · stats ·
// hash. The two versions currently being compared get a highlighted border.
function versionRow(v: any, noByHash: Record<string, number>, chg: string, selA: string, selB: string): string {
  var hash = vid(v);
  var errRate = pct(v.errorCalls || 0, v.calls || 0);
  var byRate = v.outcomes && v.outcomes.classified ? pct(v.outcomes.bypassed, v.calls || 0) : '—';
  var cmp = hash && (hash === selA || hash === selB) ? ' skd-vrow-cmp' : '';
  var span = dayOf(v.firstSeenAt) + ' → ' + (v.current ? 'now' : dayOf(v.lastSeenAt));
  // Adopter emails: show at most two, then "+N"; the full list is in the hover
  // title so a widely-adopted version stays one short line.
  var who = '';
  if (v.adopterEmails && v.adopterEmails.length) {
    var em: string[] = v.adopterEmails;
    var label = em.length <= 2
      ? em.map(esc).join(', ')
      : em.slice(0, 2).map(esc).join(', ') + ' <span class="skd-vmore">+' + (em.length - 2) + '</span>';
    who = '<div class="skd-vwho" title="' + esc(em.join(', ')) + '">' + label + '</div>';
  }
  // Hash cell links to the exact commit on GitHub when this version is git-tracked;
  // otherwise it's the plain folder-hash identity (today's behavior).
  var hashCell = v.sourceRepo && v.sourceCommit
    ? '<a class="skd-vhash skd-vcommit" href="' + commitHref(v.sourceRepo, v.sourceCommit, v.sourcePath) + '" target="_blank" rel="noopener" title="Commit ' + esc(v.sourceCommit) + '">' + esc(shortHash(v.sourceCommit)) + ' ↗</a>'
    : '<div class="skd-vhash">' + esc(shortHash(hash)) + '</div>';
  return '<div class="skd-vrow' + cmp + '">' +
    '<div class="skd-vtag">' + esc(vLabel(v, noByHash)) + '</div>' +
    '<div class="skd-vmain">' +
    '<div class="skd-vspan">' + esc(span) + (chg || '') + standingChip(v) + '</div>' +
    '<div class="skd-vstats">' + num(v.calls) + ' calls · ' + fmtWk(v.callsPerWeek) + ' · ' + errRate + ' errored · ' + byRate + ' bypassed</div>' +
    '</div>' + who + hashCell + '</div>';
}

// The description + body diffs (each scrollable), plus the extras-changed note.
function driftChangeHtml(drift: any): string {
  var html = '';
  if ((drift.descBefore || '') !== (drift.descAfter || '')) {
    html += '<div class="skd-diff-h">Description</div>' + diffHtml(drift.descBefore || '', drift.descAfter || '');
  }
  var bodyChanged = (drift.bodyBefore || '') !== (drift.bodyAfter || '');
  html += '<div class="skd-diff-h">Body' + (bodyChanged ? '' : ' <span class="skd-diff-sum">body unchanged</span>') + '</div>';
  if (bodyChanged) html += diffHtml(drift.bodyBefore || '', drift.bodyAfter || '');
  if (drift.extrasChanged) html += '<div class="skd-delta-note">Other files in the skill folder also changed (not shown).</div>';
  return html;
}

// A before→after metric cell; the "after" is bold and coloured red when worse.
function metricCmp(label: string, bs: string, as: string, dir: string): string {
  return '<div class="skd-dmetric"><div class="skd-dmetric-l">' + esc(label) + '</div>' +
    '<div class="skd-dmetric-v">' + esc(bs) + ' <span class="skd-arrow2">→</span> <b class="' + dir + '">' + esc(as) + '</b></div></div>';
}
function worseIfUp(a: number, b: number): string { return a > b ? 'skd-worse' : a < b ? 'skd-better' : ''; }
function betterIfUp(a: number, b: number): string { return a > b ? 'skd-better' : a < b ? 'skd-worse' : ''; }

function driftMetrics(drift: any): string {
  var b = drift.before || {}, a = drift.after || {};
  return '<div class="skd-cmp2">' +
    metricCmp('Own-call errors', fracPct(b.errorRate), fracPct(a.errorRate), worseIfUp(a.errorRate || 0, b.errorRate || 0)) +
    metricCmp('Bypassed by agent', fracPct(b.bypassRate), fracPct(a.bypassRate), worseIfUp(a.bypassRate || 0, b.bypassRate || 0)) +
    metricCmp('Traction', fmtWk(b.callsPerWeek), fmtWk(a.callsPerWeek), betterIfUp(a.callsPerWeek || 0, b.callsPerWeek || 0)) +
    '</div>';
}

// Canonical-relative headline: of the members currently on a git-tracked version
// we could anchor, what share is on the repo's canonical commit vs. behind vs.
// carrying local edits. Empty (renders nothing) when no version is anchored —
// then the card reads exactly as the pre-provenance, hash-only view.
function canonicalSummary(versions: any[]): string {
  var onC = 0, behind = 0, div = 0, tot = 0;
  versions.forEach(function (v: any) {
    // Only anchorable versions count — untracked (no canonical resolved, or off
    // the default branch) has no denominator claim, matching its blank chip.
    if (!v.current || !v.standing || v.standing === 'untracked') return;
    var a = v.adopters || 0;
    tot += a;
    if (v.standing === 'canonical') onC += a;
    else if (v.standing === 'behind') behind += a;
    else if (v.standing === 'diverged') div += a;
  });
  if (!tot) return '';
  var parts = ['<b>' + pct(onC, tot) + '</b> on canonical'];
  if (behind) parts.push(num(behind) + ' behind');
  if (div) parts.push(num(div) + ' with local edits');
  return '<p class="skd-canon">' + parts.join(' · ') + '</p>';
}

// 5. Version drift: A→B compare selects, a highlighted comparison card (diffs +
// before/after rates), and the one-line version list (newest first).
function versionsCard(d: any): string {
  var versions = d.versions || [];
  if (!versions.length) return '';
  var drift = d.drift;

  // Number versions oldest→newest (V1..Vn) so labels are stable; compute each
  // version's "what changed" badge against the one immediately older.
  var byAge = versions.slice().sort(function (a: any, b: any) {
    return vSeen(a) - vSeen(b) || (vid(a) < vid(b) ? -1 : vid(a) > vid(b) ? 1 : 0);
  });
  var noByHash: Record<string, number> = {};
  var chgByHash: Record<string, string> = {};
  byAge.forEach(function (v: any, i: number) {
    noByHash[vid(v)] = i + 1;
    if (i > 0) chgByHash[vid(v)] = changeBadge(byAge[i - 1], v);
  });

  var selA = vA || (drift && drift.aHash) || '';
  var selB = vB || (drift && drift.bHash) || '';

  var html = '<section class="panel skd-sec">' + secHead('Version drift', 'How behaviour changed across edited versions — correlation, not cause.') +
    canonicalSummary(versions) +
    '<p class="skd-sub">Compares the currently most-adopted version against the previously most-adopted one — or any two you pick. A rate difference is correlation, not causation.</p>';
  html += '<div class="skd-cmp">Compare ' + versionSelect('skd-vA', versions, noByHash, selA) +
    ' <span class="skd-arrow2">→</span> ' + versionSelect('skd-vB', versions, noByHash, selB) + '</div>';

  if (drift) {
    html += '<div class="skd-delta">' +
      '<div class="skd-delta-h">' + esc(labelForHash(selA, versions, noByHash)) + ' → ' + esc(labelForHash(selB, versions, noByHash)) + '</div>' +
      driftChangeHtml(drift);
    if (!drift.enoughData) {
      html += '<div class="skd-delta-note">Not enough usage on both versions to compare rates (need 3+ invocations each). The change is shown above.</div>';
    } else {
      html += driftMetrics(drift) +
        '<div class="skd-delta-note">Each version measured over its <b>own full lifetime</b>. This is <b>correlation, not causation</b> — behaviour that changed after the edit, not proof the edit caused it.</div>';
    }
    html += '</div>';
  }

  html += '<div class="skd-vlist">';
  byAge.slice().reverse().forEach(function (v: any) {
    html += versionRow(v, noByHash, chgByHash[vid(v)] || '', selA, selB);
  });
  html += '</div>';
  html += '<div class="card-note">Versions come from config snapshots, so history is only as fine-grained as your analyze cadence - edits between two runs collapse into one.</div>';
  return html + '</section>';
}

// A repo/skill name + share bar + count row, reused by where-used and
// co-occurrence.
function shareRow(cls: string, dataAttr: string, name: string, nameExtra: string, w: number, right: string): string {
  return '<div class="' + cls + '"' + dataAttr + '><div class="skd-rname">' + esc(name) + nameExtra + '</div>' +
    '<div class="skd-rbar"><span class="skd-rfill" style="width:' + w.toFixed(1) + '%"></span></div>' +
    '<div class="skd-rn">' + right + '</div></div>';
}

// 6. Where it's used: one call-scaled bar per repo.
function whereUsedCard(d: any): string {
  var repos = d.perRepo || [];
  if (!repos.length) return '';
  var max = repos.reduce(function (m: number, r: any) { return Math.max(m, r.calls || 0); }, 0) || 1;
  var html = '<section class="panel skd-sec">' + secHead("Where it's used", 'Repos where this skill ran, scaled by call count.');
  html += repos.map(function (r: any) {
    return shareRow('skd-rrow', '', r.repo || '(no repo)', '', ((r.calls || 0) / max) * 100,
      num(r.calls) + ' calls · ' + num(r.sessions) + ' sessions');
  }).join('');
  html += '<div class="card-note">Invoked in ' + num((d.usedIn || []).length) + ' repo(s).</div></section>';
  return html;
}

// 7. Frequently used with: co-occurring skills, each row navigating to it.
function cooccurCard(d: any): string {
  var co = d.cooccurrence || [];
  if (!co.length) return '';
  var maxShare = co.reduce(function (m: number, c: any) { return Math.max(m, c.share || 0); }, 0) || 1;
  var html = '<section class="panel skd-sec">' + secHead('Frequently used with', 'Skills that ran in the same sessions.');
  html += '<div class="skd-colist">' + co.map(function (c: any) {
    // "often first": this skill led in more than half of the shared sessions.
    var first = (c.precededSessions || 0) > (c.sessions || 0) / 2
      ? '<span class="skd-tag skd-tag-first">often first</span>' : '';
    var w = ((c.share || 0) / maxShare) * 100;
    return '<div class="skd-corow" data-skill="' + esc(c.name) + '">' +
      '<div class="skd-cname">' + esc(c.name) + '</div>' +
      '<div class="skd-rbar"><span class="skd-rfill" style="width:' + w.toFixed(1) + '%"></span></div>' +
      '<div class="skd-cright"><span class="skd-cmeta">' + num(c.sessions) + ' session(s) · ' +
      Math.round((c.share || 0) * 100) + '%</span>' + first + '<span class="skd-cgo">view ↗</span></div></div>';
  }).join('') + '</div>';
  html += '</section>';
  return html;
}

// 8. Details: install + first/last-use facts, laid out horizontally.
function scopeWord(s: string | null): string {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function detailsCard(d: any): string {
  var facts: Array<[string, string]> = [
    ['Install scope', scopeWord(d.installScope)],
    ['Installed in', (d.installedIn || []).join(', ') || '—'],
    ['Used in', (d.usedIn || []).join(', ') || '—'],
    ['First used', dayOf(d.firstUsedAt)],
    ['Last used', dayOf(d.lastUsedAt)],
  ];
  return '<section class="panel skd-sec">' + secHead('Details', 'Install scope and first / last use.') +
    '<div class="skd-facts">' + facts.map(function (f) {
      return '<div class="skd-fact"><div class="skd-fact-l">' + esc(f[0]) + '</div>' +
        '<div class="skd-fact-v">' + esc(f[1]) + '</div></div>';
    }).join('') + '</div></section>';
}

// 9. Invocations: a compact scrollable list; each row opens the session at the
// skill call. The row is a grid so hash / user / repo / date line up in straight
// vertical columns down the list. Version + user ride as light tags; repo/date
// stay as plain muted text so focus stays on the title (OSS-style).
function invocationsCard(d: any): string {
  var inv = d.invocations || [];
  var html = '<section class="panel skd-sec">' + secHead('Invocations', 'Every firing of this skill — open one to see it in context.') +
    '<div class="card-note" style="margin:0 0 10px">Each row opens the session at the skill call.</div>';
  if (!inv.length) return html + '<div class="empty">No invocations in this window.</div></section>';
  html += '<div class="skd-inv">' + inv.map(function (i: any) {
    // Empty placeholder spans keep the columns aligned when a value is missing.
    // A git-tracked firing links to the exact commit it ran (stopPropagation so
    // the click opens GitHub, not the session the row otherwise navigates to).
    var vt;
    if (i.sourceRepo && i.sourceCommit) {
      // A clean firing links to the EXACT commit it ran; a dirty one ran local
      // edits on TOP of that commit, so its content isn't at any commit — mark it
      // `+` and say "based on" so the link reads as an anchor, not an exact match.
      var dirtyRun = i.sourceDirty === true;
      var label = esc(shortHash(i.sourceCommit)) + (dirtyRun ? '+' : '') + ' ↗';
      var title = (dirtyRun ? 'Ran local edits based on commit ' : 'Ran commit ') + esc(i.sourceCommit);
      vt = '<a class="skd-itag num skd-icommit' + (dirtyRun ? ' skd-idirty' : '') + '" href="' + commitHref(i.sourceRepo, i.sourceCommit, i.sourcePath) +
        '" target="_blank" rel="noopener" onclick="event.stopPropagation()" title="' + title + '">' + label + '</a>';
    } else if (i.folderHash) {
      vt = '<span class="skd-itag num">' + esc(shortHash(i.folderHash)) + '</span>';
    } else {
      vt = '<span></span>';
    }
    var ut = i.userEmail ? '<span class="skd-itag">' + esc(i.userEmail) + '</span>' : '<span></span>';
    var repo = '<span class="skd-irepo">' + esc(i.repo || '') + '</span>';
    var date = '<span class="skd-idate">' + esc(dayOf(i.ts)) + '</span>';
    var err = i.isError ? '<span class="skd-ierr" title="errored">●</span>' : '<span></span>';
    // Inaccessible session (RBAC) → greyed + inert, no session link.
    var locked = i.accessible === false;
    var attr = locked ? '' : ' data-session="' + esc(i.sessionId) + '" data-idx="' + esc(String(i.idx)) + '"';
    var go = locked ? '<span class="skd-ilock" title="You don\'t have access to this session">restricted</span>' : '<span class="skd-igo">open ↗</span>';
    return '<div class="skd-irow' + (locked ? ' skd-locked' : '') + '"' + attr + '>' +
      '<span class="skd-ititle">' + esc(i.title || '(untitled)') + '</span>' +
      vt + ut + repo + date + err + go + '</div>';
  }).join('') + '</div></section>';
  return html;
}

// Mount the shared scope bar into the detail's container. A change writes the
// hash (so the team lands in the URL) and re-fetches THIS skill under the new
// scope — the roster's mount does the same for its list.
function mountSkillsDetailScope(): void {
  var fb = document.getElementById('skills-detail-fb');
  if (fb) mountScopeBar(fb as HTMLElement, function () {
    syncHash({ replace: true });
    var el = $('#skills');
    if (el && state.skills.name) renderDetail(el, state.skills.name);
  });
}

function wireDetail(el: any): void {
  wireWinSeg();
  wireBack();
  mountSkillsDetailScope();
  wireTrendTooltip();
  // Version-compare selects: set the module vars and re-fetch in place.
  var selA = document.getElementById('skd-vA') as HTMLSelectElement | null;
  var selB = document.getElementById('skd-vB') as HTMLSelectElement | null;
  function onCmp(): void {
    vA = selA ? selA.value : '';
    vB = selB ? selB.value : '';
    reloadDetail();
  }
  if (selA) selA.onchange = onCmp;
  if (selB) selB.onchange = onCmp;
  // Evidence "Show N more" reveals the rest of the list.
  var more = document.getElementById('skd-ev-more');
  if (more) more.onclick = function () {
    Array.prototype.forEach.call(el.querySelectorAll('.skd-ev-rest'), function (r: HTMLElement) { r.classList.add('on'); });
    more!.style.display = 'none';
  };
  // Rows that open a session — focused on the exact tool call when we know its
  // idx, so the transcript scrolls straight to where the skill fired.
  Array.prototype.forEach.call(el.querySelectorAll('[data-session]'), function (t: HTMLElement) {
    t.onclick = function () {
      var idxAttr = t.getAttribute('data-idx');
      var focus = idxAttr != null ? { toolTarget: parseInt(idxAttr, 10) } : undefined;
      openDetail(t.getAttribute('data-session'), focus);
    };
  });
  // Co-occurrence rows navigate to that skill.
  Array.prototype.forEach.call(el.querySelectorAll('[data-skill]'), function (t: HTMLElement) {
    t.onclick = function () { nav(t.getAttribute('data-skill') || ''); };
  });
}

function wireBack(): void {
  var b = document.getElementById('skillBack');
  if (b) b.onclick = function () { nav(''); };
}
